// src/components/core/AccessLevelProvider.js
import React, { useEffect, useState, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { functionApiScopes } from '../../authConfig';
import Loading from './Loading';
import AccessContext from '../../context/AccessContext';

const AccessLevelProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = accounts.length > 0;

  // App state
  const [availableRoutes, setAvailableRoutes] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  const refreshTimeoutRef = useRef(null);

  // -------------
  // scheduleTokenRefresh
  // -------------
  const scheduleTokenRefresh = (response) => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    if (!response.expiresOn) {
      // fallback: refresh every 5 min
      const fallbackRefresh = 5 * 60 * 1000;
      refreshTimeoutRef.current = setTimeout(() => {
        acquireTokenSilently();
      }, fallbackRefresh);
      return;
    }

    const expiresOn = response.expiresOn; // a Date object
    const offset = 2 * 60 * 1000; // Refresh 2 min before expiration
    const refreshTime = expiresOn.getTime() - Date.now() - offset;

    if (refreshTime <= 0) {
      acquireTokenSilently();
    } else {
      refreshTimeoutRef.current = setTimeout(() => {
        acquireTokenSilently();
      }, refreshTime);
    }
  };

  // -------------
  // acquireTokenSilently
  // -------------
  const acquireTokenSilently = async () => {
    const account = accounts[0];
    const tokenRequest = {
      scopes: functionApiScopes,
      account,
    };

    try {
      const response = await instance.acquireTokenSilent(tokenRequest);
      setAccessToken(response.accessToken);
      scheduleTokenRefresh(response);
      return response.accessToken;
    } catch (error) {
      if (error.name === 'InteractionRequiredAuthError') {
        // fallback => popup
        try {
          const response = await instance.acquireTokenPopup(tokenRequest);
          setAccessToken(response.accessToken);
          scheduleTokenRefresh(response);
          return response.accessToken;
        } catch (popupError) {
          console.error('Token popup failed:', popupError);
          setAvailableRoutes([]);
          setLoading(false);
          return null;
        }
      } else {
        console.error('Token acquisition failed:', error);
        setAvailableRoutes([]);
        setLoading(false);
        return null;
      }
    }
  };

  // -------------
  // callCheckUserFunction
  // -------------
  const callCheckUserFunction = async (token) => {
    try {
      const apiUrl = process.env.REACT_APP_CHECK_USER;
      const resp = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ token }), // pass token in body
      });

      if (resp.ok) {
        const data = await resp.json();
        // Expecting { user: {...}, routes: [...] }
        setAvailableRoutes(data.routes || []);
        setUser(data.user || null);
      } else {
        console.error('Function call failed, status:', resp.status);
        setAvailableRoutes([]);
        setUser(null);
      }
    } catch (err) {
      console.error('Function call error:', err);
      setAvailableRoutes([]);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // -------------
  // useEffect => Initialize
  // -------------
  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      if (inProgress !== 'none') return; // Wait for MSAL to complete

      if (isAuthenticated) {
        const token = await acquireTokenSilently();
        if (token && isMounted) {
          await callCheckUserFunction(token);
        }
      } else {
        // Not Auth => attempt login
        try {
          await instance.loginRedirect({ scopes: functionApiScopes });
        } catch (loginError) {
          console.error('Login failed:', loginError);
          if (isMounted) {
            setAvailableRoutes([]);
            setLoading(false);
          }
        }
      }
    };

    initialize();

    return () => {
      isMounted = false;
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, inProgress, instance]);

  if (loading || inProgress !== 'none') {
    return <Loading />;
  }

  return (
    <AccessContext.Provider
      value={{
        user,
        availableRoutes,
        accessToken,
      }}
    >
      {children}
    </AccessContext.Provider>
  );
};

export default AccessLevelProvider;
